// Web port of android/.../ui/SettingsDrawer.kt — the operator drawer.
//
// Opened with a long-press anywhere on the page (touch + mouse) or the `m`
// key, the web equivalents of the Android long-press / remote-MENU triggers
// (see useOperatorMenu in App.tsx). Unlike the Android drawer it drops every
// setting that has no meaning in a browser kiosk — auto-launch on boot,
// OEM auto-start, set-as-home, hard process exit, and the APK update check
// are all native-only. What's left is organised as three collapsible cards,
// same shape as the Android drawer: (1) Status & info — connectivity, screen
// identity (name + branch, never the raw id) and device detail, with the live
// connection state kept in the header even when collapsed; (2) Options — the
// three actions a browser can honor (refresh content, toggle fullscreen,
// reload); (3) Content downloads — the grouped now-playing asset list.
//
// The panel is a matte, semi-transparent frosted-dark glass (translucent black
// + backdrop-blur) over a dimmed, lightly-blurred backdrop, so the live content
// reads softly through the open drawer instead of a flat black modal.

import { useEffect, useState } from 'react'
import type { PlayerState } from '../state/usePlayerState'
import { deviceInfo } from '../lib/deviceInfo'
import {
  enterFullscreen,
  isFullscreen,
  onFullscreenChange,
} from '../lib/fullscreen'

interface Props {
  open: boolean
  onClose: () => void
  state: PlayerState
  onRefresh: () => void
  // display_settings.hide_player_controls. Drops the fullscreen row from
  // Options; Refresh and Reload stay, since those are operational recovery and
  // nothing to do with browser chrome.
  hideFullscreen?: boolean
}

// ── Inline icons (stroke-only, match Lucide) — the webplayer deliberately
// avoids pulling lucide-react, same as WifiBadge. ────────────────────────────
type IconProps = { className?: string }

function RefreshIcon({ className }: IconProps) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  )
}
function FullscreenIcon({ className, exit }: IconProps & { exit?: boolean }) {
  return exit ? (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" />
      <path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />
    </svg>
  ) : (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  )
}
function ReloadIcon({ className }: IconProps) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
    </svg>
  )
}
function WifiIcon({ online }: { online: boolean }) {
  return online ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.55a11 11 0 0 1 14.08 0" /><path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" /><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" /><path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  )
}

// ── Content summary — the web port of Android's DownloadsCard. The browser
// caches assets itself (no DownloadManager), so every assigned asset reads
// "Ready" — exactly how the Android card shows already-cached items, the
// visible proof that existing content isn't re-downloaded. Assets are grouped
// into Content / Audio / Stop screens sections, plus a Transitions info row, so
// the operator can see every channel the screen pulls. ───────────────────────
interface DownloadRowData { name: string; ready: boolean }
type SectionKind = 'content' | 'audio' | 'stop' | 'transition'
// The active channel schedule window (either bound nullable), shown inline
// next to each content piece so the operator can see when it starts / ends.
interface ScheduleWindow { start: string | null; end: string | null }
// `info` sections (Transitions) show a setting rather than a downloadable
// asset, so they render without the ready dot and don't count toward totals.
interface DownloadSection {
  kind: SectionKind
  title: string
  rows: DownloadRowData[]
  info?: boolean
  window?: ScheduleWindow | null
}
interface ContentSummary {
  orientation: string
  layoutName: string
  total: number
  ready: number
  sections: DownloadSection[]
}

function formatTransition(key?: string | null): string {
  if (!key || key === 'default') return 'Default'
  return key.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// A channel window from the manifest, or null when the occupant is permanent.
function pickWindow(start?: string | null, end?: string | null): ScheduleWindow | null {
  return start || end ? { start: start ?? null, end: end ?? null } : null
}

// Compact schedule label for a piece — local device time. "10:30–18:00" when
// both bounds are set, else "Ends 18:00" / "From 10:30". Null when permanent.
function formatSchedule(win?: ScheduleWindow | null): string | null {
  if (!win) return null
  const hm = (iso: string) =>
    new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (win.start && win.end) return `${hm(win.start)}–${hm(win.end)}`
  if (win.end) return `Ends ${hm(win.end)}`
  if (win.start) return `From ${hm(win.start)}`
  return null
}

function summarizeContent(state: PlayerState): ContentSummary | null {
  const c = state.content
  if (!c) return null
  const rotation = state.rotationDegrees ? ` · ${state.rotationDegrees}°` : ''
  const orientation = `${state.orientationType === 'vertical' ? 'Vertical (9:16)' : 'Horizontal (16:9)'}${rotation}`

  const MAX = 12
  const cap = (rows: DownloadRowData[]) => rows.slice(0, MAX)

  const content: DownloadRowData[] = []
  const zones = c.layout?.zones ?? []
  if (zones.length > 0) {
    for (const z of zones) for (const it of z.items) content.push({ name: it.name || `#${it.id}`, ready: true })
  } else {
    for (const it of c.items ?? []) content.push({ name: it.name || `#${it.id}`, ready: true })
  }
  const audio = (c.audio ?? []).map(a => ({ name: a.name || `Audio #${a.id}`, ready: true }))
  const stop  = (c.stop_screen?.items ?? []).map(it => ({ name: it.name || `#${it.id}`, ready: true }))

  const sections: DownloadSection[] = []
  if (content.length) sections.push({ kind: 'content', title: 'Content',      rows: cap(content), window: pickWindow(c.content_start_at, c.content_end_at) })
  if (audio.length)   sections.push({ kind: 'audio',   title: 'Audio',        rows: cap(audio),   window: pickWindow(c.audio_start_at, c.audio_end_at) })
  if (stop.length)    sections.push({ kind: 'stop',    title: 'Stop screens', rows: cap(stop),    window: pickWindow(c.stop_start_at, c.stop_end_at) })
  // Transitions is a per-screen setting, not a download — always show it.
  sections.push({ kind: 'transition', title: 'Transitions', rows: [{ name: formatTransition(c.transition), ready: true }], info: true })

  const assetRows = [...content, ...audio, ...stop]
  return {
    orientation,
    layoutName: c.layout?.name ?? 'Playlist',
    total: assetRows.length,
    ready: assetRows.filter(r => r.ready).length,
    sections,
  }
}

// ── Subsection icons (stroke-only, match the file's inline-SVG style). ────────
function SectionIcon({ kind }: { kind: SectionKind }) {
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (kind) {
    case 'audio':
      return (<svg {...common}><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>)
    case 'stop':
      return (<svg {...common}><path d="M18 11V6a2 2 0 0 0-4 0v5" /><path d="M14 10V4a2 2 0 0 0-4 0v6" /><path d="M10 10.5V6a2 2 0 0 0-4 0v8" /><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" /></svg>)
    case 'transition':
      return (<svg {...common}><path d="M8 3 4 7l4 4" /><path d="M4 7h16" /><path d="m16 21 4-4-4-4" /><path d="M20 17H4" /></svg>)
    default: // content
      return (<svg {...common}><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /></svg>)
  }
}

function formatAgo(ts: number | null): string {
  if (ts === null) return '—'
  const sec = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (sec < 60) return `${sec}s ago`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  return `${hr}h ago`
}

// Build stamp baked in by vite.config (scripts/viteVersionDefine.mjs). The
// `(import.meta as any).env` cast matches the rest of the app.
const BUILD_ENV  = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_APP_ENV
const BUILD_SHA  = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_GIT_SHA
const BUILD_TIME = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_BUILD_TIME
function buildStamp(): string {
  const parts: string[] = []
  if (BUILD_SHA) parts.push(BUILD_SHA)
  if (BUILD_TIME) {
    const d = new Date(BUILD_TIME)
    if (!Number.isNaN(d.getTime()))
      parts.push(d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }))
  }
  return parts.join(' · ')
}

export default function SettingsDrawer({ open, onClose, state, onRefresh, hideFullscreen = false }: Props) {
  // Mount-with-animation pattern: keep the node in the DOM through the
  // slide-out so the exit transition can play, then unmount.
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)
  const [fs, setFs] = useState(isFullscreen)
  // The drawer is three collapsible cards (status/info, options, downloads),
  // ALL closed by default to keep the panel tidy on open — including
  // status/info, which used to default open. The connection state still
  // shows in the status card header even when it's collapsed, so nothing
  // load-bearing is hidden by starting closed.
  const [infoOpen, setInfoOpen] = useState(false)
  const [optionsOpen, setOptionsOpen] = useState(false)
  const [downloadsOpen, setDownloadsOpen] = useState(false)

  useEffect(() => {
    if (open) { setMounted(true); setClosing(false) }
    else if (mounted) {
      setClosing(true)
      const t = window.setTimeout(() => setMounted(false), 200)
      return () => window.clearTimeout(t)
    }
  }, [open, mounted])

  useEffect(() => onFullscreenChange(() => setFs(isFullscreen())), [])

  if (!mounted) return null

  const online = state.isOnline === true
  const connLabel = state.isOnline === null ? 'Connecting…' : online ? 'Connected' : 'No connection'
  const connColor = state.isOnline === null
    ? 'var(--status-unknown)'
    : online ? 'var(--status-online)' : 'var(--status-offline)'

  const summary = summarizeContent(state)

  const toggleFullscreen = () => {
    if (isFullscreen()) { void document.exitFullscreen?.() }
    else { void enterFullscreen() }
  }

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm ${closing ? 'op-backdrop-out' : 'op-backdrop-in'}`}
      // Primary-button tap on the backdrop (outside the panel) closes —
      // matches the Android scrim. Guarded to button 0 so a right-click
      // (which opens the menu, see useOperatorMenu) doesn't close-then-reopen.
      onPointerDown={(e) => { if (e.button === 0) onClose() }}
    >
      <div
        className={`absolute inset-y-0 left-0 w-full max-w-[420px] flex flex-col
                    bg-[rgba(18,18,18,0.72)] backdrop-blur-2xl border-r border-white/10 shadow-2xl
                    ${closing ? 'op-panel-out' : 'op-panel-in'}`}
        // Swallow taps inside the panel so they don't bubble to the backdrop.
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-5 pb-3 sm:px-6 sm:pt-7 sm:pb-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[20px] sm:text-[22px] font-black leading-tight">WeTook Display</h1>
              {BUILD_ENV === 'staging' && (
                <span className="rounded bg-amber-500/20 text-amber-300 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5">
                  staging
                </span>
              )}
            </div>
            <p className="text-muted text-[13px] mt-0.5">
              v{String(deviceInfo.appVersion).replace(/-/g, ' ')} · web player
            </p>
            {/* Build stamp — git SHA + build date/time. */}
            {buildStamp() && <p className="text-muted/70 text-[11px] font-mono mt-0.5">{buildStamp()}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 -mr-1 rounded-lg p-2 text-muted hover:text-white hover:bg-white/5"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-3 sm:px-6 sm:pb-8">
          {/* ── Status & info — merged, collapsible. The header carries the
              live connection state (dot + wifi + label) so it reads at a glance
              even while collapsed; expanding reveals the screen identity and
              device detail. Screen is identified by NAME + BRANCH — the raw
              numeric id is sensitive and never shown. ──────────────────────── */}
          <section className="rounded-lg border border-white/10 bg-white/[0.04]">
            <button
              type="button"
              onClick={() => setInfoOpen(o => !o)}
              aria-expanded={infoOpen}
              className="w-full flex items-center gap-2 px-4 py-3.5 text-left"
              style={{ color: connColor }}
            >
              <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: connColor }} />
              <WifiIcon online={online} />
              <span className="text-[15px] font-semibold flex-1">{connLabel}</span>
              <ChevronIcon open={infoOpen} />
            </button>
            {infoOpen && (
              <div className="px-4 pb-4 space-y-3">
                <StatusLine k="Screen" v={state.screenName || (state.isPaired ? 'Paired' : 'Not paired')} />
                {state.branchName && <StatusLine k="Branch" v={state.branchName} />}
                <StatusLine k="Synced" v={formatAgo(state.lastFetchedAt)} />
                {state.cacheExpired && (
                  <StatusLine k="Cache" v="Expired — awaiting reconnect" warn />
                )}
                {/* Device / player detail. */}
                <div className="border-t border-white/10 pt-3 space-y-3">
                  <StatusLine k="Player" v={deviceInfo.name} />
                  <StatusLine k="Browser" v={deviceInfo.model} />
                  <StatusLine k="System" v={deviceInfo.osVersion} />
                  <StatusLine k="Version" v={`${String(deviceInfo.appVersion)} (${deviceInfo.deviceKind})`} />
                  <StatusLine
                    k="State"
                    v={!state.isPaired ? 'Unpaired' : state.isActive ? 'Active' : 'Deactivated'}
                  />
                  {/* Content metadata (not a content piece) lives here, out of
                      the downloads card so that card holds only the asset list. */}
                  {summary && <StatusLine k="Layout" v={summary.layoutName} />}
                  {summary && <StatusLine k="Orient." v={summary.orientation} />}
                </div>
              </div>
            )}
          </section>

          {/* ── Options — the web-applicable actions, collapsible. ────────── */}
          <section className="rounded-lg border border-white/10 bg-white/[0.04]">
            <button
              type="button"
              onClick={() => setOptionsOpen(o => !o)}
              aria-expanded={optionsOpen}
              className="w-full flex items-center gap-2 px-4 py-3.5 text-left"
            >
              <h2 className="text-[15px] font-semibold flex-1">Options</h2>
              <ChevronIcon open={optionsOpen} />
            </button>
            {optionsOpen && (
              <div className="px-2 pb-2 space-y-1">
                <ActionRow
                  icon={<RefreshIcon className="opacity-90" />}
                  title="Refresh content"
                  subtitle="Re-fetch the latest playlist now"
                  onActivate={() => { onRefresh(); onClose() }}
                />
                {!hideFullscreen && (
                  <ActionRow
                    icon={<FullscreenIcon className="opacity-90" exit={fs} />}
                    title={fs ? 'Exit fullscreen' : 'Enter fullscreen'}
                    subtitle={fs ? 'Show the browser chrome' : 'Hide the browser chrome'}
                    onActivate={toggleFullscreen}
                  />
                )}
                <ActionRow
                  icon={<ReloadIcon className="opacity-90" />}
                  title="Reload player"
                  subtitle="Restart the player app"
                  onActivate={() => window.location.reload()}
                />
              </div>
            )}
          </section>

          {/* ── Content downloads — collapsible, anchored at the end of the
              menu (mirrors Android's DownloadsCard). ───────────────────── */}
          <section className="rounded-lg border border-white/10 bg-white/[0.04]">
            <button
              type="button"
              onClick={() => setDownloadsOpen(o => !o)}
              aria-expanded={downloadsOpen}
              className="w-full flex items-center gap-2 px-4 py-3.5 text-left"
            >
              <h2 className="text-[15px] font-semibold flex-1">Content downloads</h2>
              {summary && <span className="text-[12px] text-muted">{summary.ready}/{summary.total}</span>}
              <ChevronIcon open={downloadsOpen} />
            </button>
            {downloadsOpen && (
              <div className="px-4 pb-4 space-y-3">
                {summary ? (
                  /* Grouped subsections only — Content / Audio / Stop screens /
                     Transitions, each with its icon. The channel's schedule
                     window (when set) shows inline next to every piece. */
                  summary.sections.map(sec => {
                    const schedule = sec.info ? null : formatSchedule(sec.window)
                    return (
                      <div key={sec.title} className="rounded-lg bg-white/[0.03] p-3 space-y-2">
                        <p className="text-[11px] uppercase tracking-wider text-muted flex items-center gap-1.5">
                          <SectionIcon kind={sec.kind} />
                          {sec.title}
                        </p>
                        {sec.rows.map((r, i) => (
                          <DownloadRow key={i} name={r.name} ready={r.ready} info={sec.info} schedule={schedule} />
                        ))}
                      </div>
                    )
                  })
                ) : (
                  <p className="text-[12px] text-muted">
                    {state.isPaired ? 'No content assigned yet.' : 'Screen not paired.'}
                  </p>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}

// Rotating chevron for the collapsible downloads header.
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={`shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

// One asset (or setting) row. A small status dot — not a full green bar —
// keeps the drawer black-and-dark with green only as a subtle accent. `info`
// rows (a transition setting) drop the dot and read "Applied".
function DownloadRow({ name, ready, info, schedule }: { name: string; ready: boolean; info?: boolean; schedule?: string | null }) {
  return (
    <div className="flex items-center gap-2">
      {!info && (
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: ready ? 'var(--status-online)' : 'rgba(255,255,255,0.25)' }}
        />
      )}
      <span className="flex-1 truncate text-[12px] text-white/85">{name}</span>
      {/* Inline per-piece schedule — the channel's active window. */}
      {schedule && <span className="text-[10px] text-white/40 shrink-0 tabular-nums">{schedule}</span>}
      <span className="text-[11px] text-muted shrink-0">
        {info ? 'Applied' : ready ? 'Ready' : 'Queued'}
      </span>
    </div>
  )
}

function StatusLine({ k, v, warn }: { k: string; v: string; warn?: boolean }) {
  return (
    <div className="flex gap-2 text-[12px]">
      <span className="w-14 shrink-0 text-muted sm:w-16">{k}</span>
      <span className={`min-w-0 flex-1 break-words ${warn ? 'text-offline font-semibold' : 'text-white/85'}`}>{v}</span>
    </div>
  )
}

function ActionRow({
  icon, title, subtitle, onActivate,
}: {
  icon: React.ReactNode
  title: string
  subtitle?: string
  onActivate: () => void
}) {
  return (
    <button
      type="button"
      onClick={onActivate}
      className="w-full flex items-center gap-3.5 rounded-lg px-3 py-3.5 text-left
                 text-white hover:bg-white/5 focus:outline-none focus-visible:ring-2
                 focus-visible:ring-brand"
    >
      <span className="shrink-0 text-white">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-medium">{title}</span>
        {subtitle && <span className="block text-[12px] text-muted">{subtitle}</span>}
      </span>
    </button>
  )
}
